import { useState, useEffect, useCallback, useRef } from 'react';
import { Wifi, WifiOff, RefreshCw, CloudOff, Check } from '../../icons/wp-icons';
import syncService from '../../services/syncService';
import { useIsMobile } from '../../hooks/useIsMobile';

export default function OnlineStatus() {
  const isMobile = useIsMobile();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(!navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncResult, setSyncResult] = useState(null);
  const hideTimeoutRef = useRef(null);
  const isSyncingRef = useRef(false);
  const onlineSinceRef = useRef(null);

  // Pending Count laden
  const loadPendingCount = useCallback(async () => {
    try {
      const count = await syncService.getPendingCount();
      setPendingCount(count);
    } catch (error) {
      console.error('Error loading pending count:', error);
    }
  }, []);

  // Banner nach Delay ausblenden
  const hideBannerAfterDelay = useCallback((delayMs = 3000) => {
    // Vorherigen Timeout clearen
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    console.log(`⏱️ Banner wird in ${delayMs}ms ausgeblendet`);

    hideTimeoutRef.current = setTimeout(() => {
      console.log('🔽 Banner wird jetzt ausgeblendet');
      setShowBanner(false);
      setSyncResult(null);
      hideTimeoutRef.current = null;
    }, delayMs);
  }, []);

  // Sync durchführen (verwendet Ref um Stale Closure zu vermeiden)
  const performSync = useCallback(async () => {
    if (!navigator.onLine || isSyncingRef.current) {
      console.log('⏭️ Sync übersprungen (offline oder bereits am synchen)');
      return null;
    }

    isSyncingRef.current = true;
    setIsSyncing(true);
    setSyncResult(null);

    try {
      console.log('🔄 Sync gestartet...');
      const result = await syncService.syncAll();
      console.log('✅ Sync abgeschlossen:', result);

      if (result.totalSynced > 0 || result.totalFailed > 0) {
        setSyncResult(result);
      }

      await loadPendingCount();
      return result;
    } catch (error) {
      console.error('Sync error:', error);
      return null;
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [loadPendingCount]);

  // Event Listener Setup - nur einmal beim Mount
  useEffect(() => {
    const handleOnline = async () => {
      console.log('🌐 Wieder online');
      setIsOnline(true);
      setShowBanner(true);
      onlineSinceRef.current = Date.now();

      // Vorherigen Timeout clearen
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      // Sync durchführen
      const result = await performSync();

      // Banner nach 3 Sekunden ausblenden (etwas länger wenn Sync-Ergebnisse angezeigt werden)
      const delay = result && (result.totalSynced > 0 || result.totalFailed > 0) ? 4000 : 3000;
      hideBannerAfterDelay(delay);
    };

    const handleOffline = () => {
      console.log('📴 Offline');
      setIsOnline(false);
      setShowBanner(true);
      onlineSinceRef.current = null;

      // Timeout clearen
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial Pending Count laden
    loadPendingCount();

    // Periodisch Pending Count aktualisieren
    const interval = setInterval(loadPendingCount, 30000);

    // Backup: Banner nach 5 Sekunden ausblenden wenn online und nicht am synchen
    const backupInterval = setInterval(() => {
      if (onlineSinceRef.current && !isSyncingRef.current) {
        const elapsed = Date.now() - onlineSinceRef.current;
        if (elapsed > 5000) {
          console.log('🔄 Backup: Banner wird ausgeblendet');
          setShowBanner(false);
          setSyncResult(null);
          onlineSinceRef.current = null;
        }
      }
    }, 1000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
      clearInterval(backupInterval);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [performSync, loadPendingCount, hideBannerAfterDelay]);

  // Manuelle Sync-Funktion
  const handleManualSync = async () => {
    if (!isOnline || isSyncing) return;
    await performSync();
  };

  // Mobile: Inline Banner der sich ins Layout einfügt
  if (isMobile) {
    return (
      <div
        className="fixed left-0 right-0 z-50 overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          maxHeight: showBanner ? '32px' : '0px',
          opacity: showBanner ? 1 : 0
        }}
      >
        <div
          className={`flex items-center justify-center gap-2 px-3 py-1.5 text-xs transition-colors duration-300 ${
            isOnline
              ? syncResult?.totalFailed > 0
                ? 'bg-orange-500 text-white'
                : 'bg-green-500 text-white'
              : 'bg-orange-500 text-white'
          }`}
        >
          {isOnline ? (
            <>
              {isSyncing ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Synchronisiere{pendingCount > 0 ? ` (${pendingCount})` : ''}...</span>
                </>
              ) : syncResult ? (
                <>
                  {syncResult.totalFailed > 0 ? (
                    <CloudOff className="w-3.5 h-3.5" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  <span>
                    {syncResult.totalSynced} synchronisiert
                    {syncResult.totalFailed > 0 && `, ${syncResult.totalFailed} fehlgeschlagen`}
                  </span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Wieder online</span>
                </>
              )}
            </>
          ) : (
            <>
              <WifiOff className="w-3.5 h-3.5" />
              <span>Offline{pendingCount > 0 ? ` • ${pendingCount} ausstehend` : ''}</span>
            </>
          )}

          {/* Manual Sync Button */}
          {isOnline && pendingCount > 0 && !isSyncing && !syncResult && (
            <button
              onClick={handleManualSync}
              className="ml-1 p-0.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <RefreshCw className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Desktop: Notification in der Ecke (nur wenn relevant)
  const shouldShowDesktop = showBanner || isSyncing || syncResult;

  if (!shouldShowDesktop) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-down">
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg transition-all ${
          isOnline
            ? syncResult?.totalFailed > 0
              ? 'bg-orange-500 text-white'
              : 'bg-green-500 text-white'
            : 'bg-orange-500 text-white'
        }`}
      >
        {isOnline ? (
          <>
            {isSyncing ? (
              // Syncing State
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <div>
                  <span className="font-medium">Synchronisiere...</span>
                  {pendingCount > 0 && (
                    <p className="text-xs text-white/80">
                      {pendingCount} Element{pendingCount > 1 ? 'e' : ''}
                    </p>
                  )}
                </div>
              </>
            ) : syncResult ? (
              // Sync Result State
              <>
                {syncResult.totalFailed > 0 ? (
                  <CloudOff className="w-5 h-5" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
                <div>
                  <span className="font-medium">
                    {syncResult.totalSynced} synchronisiert
                    {syncResult.totalFailed > 0 && `, ${syncResult.totalFailed} fehlgeschlagen`}
                  </span>
                </div>
              </>
            ) : (
              // Online State
              <>
                <Wifi className="w-5 h-5" />
                <span className="font-medium">Wieder online</span>
              </>
            )}
          </>
        ) : (
          // Offline State
          <>
            <WifiOff className="w-5 h-5" />
            <div>
              <span className="font-medium">Offline-Modus</span>
              {pendingCount > 0 ? (
                <p className="text-xs text-white/80">
                  {pendingCount} Element{pendingCount > 1 ? 'e' : ''} warten auf Sync
                </p>
              ) : (
                <p className="text-xs text-white/80">Daten werden lokal gespeichert</p>
              )}
            </div>
          </>
        )}

        {/* Manual Sync Button wenn online und pending Items */}
        {isOnline && pendingCount > 0 && !isSyncing && !syncResult && (
          <button
            onClick={handleManualSync}
            className="ml-2 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            title="Jetzt synchronisieren"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
